import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  EsgFormData,
  FORBIDDEN_INVESTMENT_OPTIONS,
  INVESTMENT_OPTIONS,
  NON_ESG_INVESTMENT_OPTIONS,
  OwnerEsgProfile,
  SUSTAINABILITY_OPTIONS,
  TAXONOMY_OPTIONS,
} from '@app/components/pr-esg-criteria/esg-profile.types';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { UserDao } from '@app/dao/user/user.dao';
import { of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'ea-esg-form',
  templateUrl: './ea-esg-form.component.html',
  styleUrls: ['./ea-esg-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaEsgFormComponent),
    },
  ],
  standalone: false,
})
export class EaEsgFormComponent extends PrFormAbstract implements OnInit {
  @Input() ownerId: number;

  doneSubject = new Subject<OwnerEsgProfile>();

  // Options
  taxonomyOptions = TAXONOMY_OPTIONS;
  sustainabilityOptions = SUSTAINABILITY_OPTIONS;
  investmentOptions = INVESTMENT_OPTIONS;
  nonEsgInvestmentOptions = NON_ESG_INVESTMENT_OPTIONS;
  forbiddenInvestmentOptions = FORBIDDEN_INVESTMENT_OPTIONS;

  // Info texts
  taxonomyInfo =
    "C'est-à-dire avec l'un des 6 objectifs suivants, sans nuire aux 5 autres : atténuation du changement climatique, adaptation au changement climatique, utilisation durable et protection des ressources aquatiques et marines, transition vers une économie circulaire, prévention et réduction de la pollution et protection et restauration de la biodiversité et des écosystèmes.";
  sustainabilityInfo =
    "Selon la réglementation « SFDR », c'est un investissement dans une activité économique contribuant à un objectif environnemental ou social sans causer de préjudice significatif à d'autres objectifs environnementaux ou sociaux, tout en veillant à ce que l'entreprise dans laquelle est effectué l'investissement applique des bonnes pratiques de gouvernance.";

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
  ) {
    super(formConstructor);
  }

  /**
   * Generate the form with entity data
   * Called by initForm() from PrFormAbstract
   */
  generate(entity: OwnerEsgProfile | null): UntypedFormGroup {
    const investments = entity?.investments?.length
      ? entity.investments
      : entity?.includeESG
        ? ['null']
        : [];
    const nonESGInvestments = entity?.nonESGInvestments?.length
      ? entity.nonESGInvestments
      : entity?.includeNonESG
        ? ['null']
        : [];
    const investmentsForbiden = entity?.investmentsForbiden?.length
      ? entity.investmentsForbiden
      : entity?.includeESG
        ? ['null']
        : [];

    return this.formBuilder.group({
      taxonomy: [entity?.taxonomy?.toString() || '0'],
      sustainability: [entity?.sustainability?.toString() || '0'],
      investments: [investments],
      includeNonESG: [entity?.includeNonESG || false],
      nonESGInvestments: [nonESGInvestments],
      investmentsForbiden: [investmentsForbiden],
      acceptRecommendations: [entity?.acceptRecommendations || false],
    });
  }

  /**
   * Extract form data for API submission
   */
  extract(): Partial<OwnerEsgProfile> {
    const formData = this.form.value as EsgFormData;

    return {
      ownerId: this.ownerId,
      includeESG: true,
      taxonomy: parseInt(formData.taxonomy, 10) || 0,
      sustainability: parseInt(formData.sustainability, 10) || 0,
      investments: formData.investments.filter(v => v !== 'null') as any[],
      includeNonESG: formData.includeNonESG,
      nonESGInvestments: formData.nonESGInvestments.filter(
        v => v !== 'null',
      ) as any[],
      investmentsForbiden: formData.investmentsForbiden.filter(
        v => v !== 'null',
      ) as any[],
      acceptRecommendations: formData.acceptRecommendations,
    };
  }

  ngOnInit(): void {
    // Form is already initialized via initForm() called by prFormService
    // If form is not yet initialized (fallback), initialize with empty data
    if (!this.form || Object.keys(this.form.controls).length === 0) {
      this.form = this.generate(null);
    }
  }

  get includeNonESG(): boolean {
    return this.form.get('includeNonESG')?.value || false;
  }

  onToggleChange(field: string, value: string): void {
    this.form.get(field)?.setValue(value);
  }

  onChecklistChange(field: string, optionId: string, checked: boolean): void {
    const currentValues: string[] = this.form.get(field)?.value || [];

    // Handle "null" (Aucun) option
    if (optionId === 'null') {
      if (checked) {
        this.form.get(field)?.setValue(['null']);
      } else {
        this.form.get(field)?.setValue([]);
      }
      return;
    }

    // Remove "null" if selecting another option
    let newValues = currentValues.filter(v => v !== 'null');

    if (checked) {
      if (!newValues.includes(optionId)) {
        newValues.push(optionId);
      }
    } else {
      newValues = newValues.filter(v => v !== optionId);
    }

    this.form.get(field)?.setValue(newValues);
  }

  isOptionSelected(field: string, optionId: string): boolean {
    const values: string[] = this.form.get(field)?.value || [];
    return values.includes(optionId);
  }

  onSwitchChange(field: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.form.get(field)?.setValue(target.checked);

    if (field === 'includeNonESG') {
      if (target.checked) {
        // Auto-select "Aucun" when includeNonESG is turned on
        this.form.get('nonESGInvestments')?.setValue(['null']);
      } else {
        // Reset nonESGInvestments if includeNonESG is turned off
        this.form.get('nonESGInvestments')?.setValue([]);
      }
    }
  }

  onSubmit(): void {
    if (this.loading) return;

    this.loading = true;
    const payload = this.extract();

    this.prFormService.loading();
    this.userDao
      .saveEsgProfile(this.ownerId, payload)
      .pipe(
        catchError(error => {
          this.notificationService.error(
            'Erreur lors de la sauvegarde du profil ESG.',
          );
          this.loading = false;
          this.prFormService.loaded();
          return of(null);
        }),
      )
      .subscribe((result: any) => {
        this.loading = false;
        this.prFormService.loaded();
        if (result) {
          this.notificationService.success(
            'Profil ESG sauvegardé avec succès.',
          );
          this.doneSubject.next(result);
        }
      });
  }

  onCancel(): void {
    this.prFormService.close();
  }
}
